import { useCallback, useMemo, useRef, useState } from 'react'
import rangy from 'rangy'
import 'rangy/lib/rangy-classapplier'
import 'rangy/lib/rangy-textrange'
import 'rangy/lib/rangy-highlighter'
import './highlighter.d'
import { announce } from '@app/components/common/ScreenReaderAnnouncer'
import { useHighlightsRestore } from './useHighlightsRestore'
import {
  addHighlightMarkers,
  clearMarkErasable,
  removeErasableStubs,
  removeHighlightMarkers,
  removeMarksFromContainer,
  RESTORE_DELAY_MS,
  saveHighlights,
  withSaveBlock,
} from '../highlighter'

// a11y: spoken via the live region on every apply/erase. Kept here (not in useHighlightMode) so all
// entry points announce once — the STAAR context menu calls these methods directly (Ready#7707).
const HIGHLIGHT_ADDED_ANNOUNCEMENT = 'Highlighted'
const HIGHLIGHT_REMOVED_ANNOUNCEMENT = 'Highlight removed'

export const createHighlighter = (className: string, tagName = 'span', containerId?: string): Highlighter => {
  const highlighter = rangy.createHighlighter()

  highlighter.addClassApplier(
    rangy.createClassApplier(className, {
      ignoreWhiteSpace: true,
      elementTagName: tagName,
      tagNames: [tagName],
      elementProperties: {
        ...(containerId && { 'data-container': containerId }),
      },
    }),
  )

  return highlighter
}

export const useHighlighter = (className: string, key: string, containerId: string) => {
  const highlighterRef = useRef<Highlighter>(createHighlighter(className, 'span', containerId))
  const [hasHighlights, setHasHighlights] = useState<boolean>(false)
  const isRestoringRef = useRef(false)
  const isSavingRef = useRef(false)

  const onRestoreHighlights = useCallback(() => {
    if (isRestoringRef.current || isSavingRef.current) return

    isRestoringRef.current = true
    const container = document.getElementById(containerId)

    if (!container) {
      isRestoringRef.current = false
      return
    }

    removeMarksFromContainer(container, className)

    const serializedHighlight = localStorage.getItem(key)
    setHasHighlights(!!serializedHighlight)

    if (!serializedHighlight) {
      isRestoringRef.current = false
      return
    }

    setTimeout(() => {
      try {
        highlighterRef.current.deserialize(serializedHighlight)
        // a11y: decorate restored marks so a screen reader announces them on read.
        // Re-fetch the container in case the node was swapped during the restore delay.
        const currentContainer = document.getElementById(containerId)
        if (currentContainer) addHighlightMarkers(currentContainer, className)
      } catch (e) {
        console.error(`[Highlighter] Failed to restore highlights for ${containerId}:`, e)
      }
      isRestoringRef.current = false
    }, RESTORE_DELAY_MS)
  }, [key, containerId, className])

  useHighlightsRestore(containerId, onRestoreHighlights)

  const isValidSelection = useCallback(
    (selection: Selection): boolean => {
      if (selection.isCollapsed) return false
      const container = document.getElementById(containerId)
      return container ? selection.containsNode(container, true) : false
    },
    [containerId],
  )

  const onHighlightSelection = useCallback(() => {
    if (isRestoringRef.current) return

    const selection = rangy.getSelection()
    if (!isValidSelection(selection)) return

    const container = document.getElementById(containerId)

    withSaveBlock(isSavingRef, () => {
      const highlighter = highlighterRef.current
      // a11y: strip sr markers so rangy computes and serializes clean character offsets
      if (container) removeHighlightMarkers(container)

      highlighter.highlightSelection(className, {
        containerElementId: containerId,
      })

      setHasHighlights(true)
      saveHighlights(highlighter, key)
      selection.removeAllRanges()

      // a11y: re-add sr markers now that rangy is done serializing
      if (container) addHighlightMarkers(container, className)
    })

    announce(HIGHLIGHT_ADDED_ANNOUNCEMENT)
  }, [key, className, containerId, isValidSelection])

  const onRemoveHighlightsFromSelection = useCallback(() => {
    const selection = rangy.getSelection()
    const highlighter = highlighterRef.current
    const container = document.getElementById(containerId)

    withSaveBlock(isSavingRef, () => {
      // a11y: strip erase-button attrs from marks the selection touches so rangy can fully unwrap
      // them (Ctrl+Shift+H erase while in erase mode); untouched marks keep their affordances.
      const sel = window.getSelection()
      const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null
      if (container && range) {
        container.querySelectorAll(`span.${className}`).forEach(mark => {
          if (range.intersectsNode(mark)) clearMarkErasable(mark)
        })
      }

      // a11y: strip sr markers before rangy unwraps the mark, then re-decorate what remains
      if (container) removeHighlightMarkers(container)

      highlighter.removeHighlightsFromSelection(containerId)
      // a11y: unwrap any split-highlight fragment rangy left with its erase attrs (no dead tab stop)
      if (container) removeErasableStubs(container, className)
      saveHighlights(highlighter, key)
      selection.removeAllRanges()

      if (container) addHighlightMarkers(container, className)
    })

    announce(HIGHLIGHT_REMOVED_ANNOUNCEMENT)
  }, [containerId, key, className])

  const onRemoveHighlight = useCallback(
    (mark?: Element) => {
      // a11y: no Element arg = the STAAR context-menu "Unhighlight Selection" action (called arg-less;
      // the menu wrappers strip the click event), which historically removed every highlight
      // intersecting the selection. Delegate to preserve that. Guarding on `instanceof Element` (not
      // just truthiness) means a future direct onClick wiring that passes an event still takes the
      // range path instead of throwing. The single-mark path below is keyboard/click erase (Ready#7707).
      if (!(mark instanceof Element)) {
        onRemoveHighlightsFromSelection()
        return
      }

      const highlighter = highlighterRef.current
      const selection = rangy.getSelection()
      const container = document.getElementById(containerId)

      withSaveBlock(isSavingRef, () => {
        // a11y: strip sr markers before rangy unwraps the mark, so markers are not orphaned
        if (container) removeHighlightMarkers(container)

        // a11y: strip erase-button attrs so rangy can unwrap the focused fragment
        clearMarkErasable(mark)
        try {
          const highlight = highlighter.getHighlightForElement(mark)
          if (highlight) {
            highlight.unapply()
            const index = highlighter.highlights.indexOf(highlight)
            if (index > -1) {
              highlighter.highlights.splice(index, 1)
            }
          }
        } catch (e) {
          console.error('[Highlighter] Failed to remove highlight:', e)
        }

        // a11y: a highlight split across an inline element leaves fragments rangy can't unwrap (they
        // kept the erase-button attrs) as class-less stubs; unwrap them so no dead tab stop lingers.
        if (container) removeErasableStubs(container, className)

        saveHighlights(highlighter, key)

        if (highlighter.highlights.length === 0) {
          setHasHighlights(false)
        }

        selection.removeAllRanges()

        // a11y: re-decorate the marks that remain
        if (container) addHighlightMarkers(container, className)
      })

      announce(HIGHLIGHT_REMOVED_ANNOUNCEMENT)
    },
    [containerId, key, className, onRemoveHighlightsFromSelection],
  )

  const onResetHighlight = useCallback(() => {
    // a11y: strip sr markers so removeAllHighlights does not orphan them when it unwraps marks
    const container = document.getElementById(containerId)
    if (container) removeHighlightMarkers(container)

    highlighterRef.current.removeAllHighlights()
    setHasHighlights(false)
    localStorage.removeItem(key)
  }, [key, containerId])

  const isSelectionHighlighted = useCallback(() => {
    const anchorNode = window.getSelection()?.anchorNode
    if (!anchorNode) return false

    // a11y: detect the enclosing highlight from the DOM — the sr markers shift the char offsets
    // rangy's getHighlightForElement relies on, so it under-reports and Ctrl+Shift+H won't erase.
    const element =
      anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : (anchorNode as Element)

    return !!element?.closest(`span.${className}`)
  }, [className])

  // a11y: memoize so the returned object keeps a stable identity across renders — otherwise every
  // consumer effect (getHighlighterBySelection/ByElement and their listeners) re-subscribes each render.
  return useMemo(
    () => ({
      hasHighlights,
      onHighlightSelection,
      onResetHighlight,
      onRemoveHighlight,
      onRemoveHighlightsFromSelection,
      isSelectionHighlighted,
    }),
    [
      hasHighlights,
      onHighlightSelection,
      onResetHighlight,
      onRemoveHighlight,
      onRemoveHighlightsFromSelection,
      isSelectionHighlighted,
    ],
  )
}
